import React, {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useTransition,
} from 'react';

import type { NavigationTransition } from '@shared/router/location';
import { logRouterError, reportRouterError } from '@shared/router/logger';

interface RouterState {
	currentPath?: string;
}

export enum RouterAction {
	Reset = 'reset',
	CurrentRoute = 'currentPath',
	Refresh = 'refresh',
}

export type Action =
	| { type: RouterAction.Reset; payload: RouterState }
	| { type: RouterAction.CurrentRoute; payload: RouterState }
	| { type: RouterAction.Refresh; payload: RouterState };

type RouterPayload = number | string | null;

interface RouterStateDispatch {
	state: RouterState;
	dispatch?: React.Dispatch<Action>;
	emit: (
		type: RouterAction,
		payload: RouterPayload,
		transition?: NavigationTransition
	) => void;
	isPending: boolean;
}

// Belt-and-suspenders ceiling: if React never settles (data never resolves and
// the error path doesn't fire), release the held "before" snapshot anyway so the
// cross-fade can't stick. Mirrors the suspense fetch timeout in useEndpoint.
const VIEW_TRANSITION_TIMEOUT_MS = 15000;

function supportsViewTransition(): boolean {
	if (typeof document === 'undefined') {
		return false;
	}

	return typeof document.startViewTransition === 'function';
}

function pathnameOf(path: string): string {
	const queryIndex = path.indexOf('?');

	if (queryIndex === -1) {
		return path;
	}

	return path.slice(0, queryIndex);
}

function getInitialPath(): string {
	try {
		if (typeof window !== 'undefined' && window.location) {
			return window.location.pathname;
		}
	} catch {}

	return '';
}

const initialState: RouterState = {
	currentPath: getInitialPath(),
};

const RouterContext = createContext<RouterStateDispatch>({
	state: initialState,
	emit: () => undefined,
	isPending: false,
});

interface RouterHookResponse extends RouterState, RouterStateDispatch {
	reset: () => void;
	setCurrentPath: (
		currentPath: string,
		transition?: NavigationTransition
	) => void;
	refresh: () => void;
}

export const useRouterState = (): RouterHookResponse => {
	const { state, emit, isPending } = useContext(RouterContext);

	const reset = (): void => emit(RouterAction.Reset, null);

	const setCurrentPath = (
		currentPath: string,
		transition?: NavigationTransition
	): void => {
		if (state.currentPath !== currentPath) {
			emit(RouterAction.CurrentRoute, currentPath, transition);
		}
	};

	const refresh = (): void => {
		emit(RouterAction.Refresh, state.currentPath ?? '');
	};

	return {
		...state,
		isPending,
		reset,
		setCurrentPath,
		refresh,
	} as RouterHookResponse;
};

export const useCurrentRoutePath = (): string => {
	return useRouterState().currentPath ?? '/';
};

/**
 * True while a route transition is in flight — i.e. the next view is suspended
 * waiting on its data and the current view is being held on screen. Drives the
 * global navigation progress bar.
 */
export const useNavigationPending = (): boolean => {
	return useContext(RouterContext).isPending;
};

interface StateProviderProps {
	children: ReactNode;
}

const reducer = (state: RouterState, action: Action) => {
	const mutateState = (
		state: RouterState,
		nextState: RouterState
	): RouterState => ({
		...state,
		...nextState,
	});

	const actionType = action.type;

	switch (actionType) {
		case RouterAction.Reset:
			return { ...initialState };
		case RouterAction.CurrentRoute:
			return mutateState(state, action.payload);
		case RouterAction.Refresh:
			return mutateState(state, action.payload);
		default:
			throw new Error(`Invalid action '${actionType}'.`);
	}
};

const RouterStateProvider = ({ children }: StateProviderProps): ReactNode => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const [isPending, startTransition] = useTransition();

	// Resolves the in-flight view transition's promise — i.e. tells the View
	// Transition API the new view has committed so it can take the "after"
	// snapshot and animate. Null when no transition is pending.
	const pendingResolveRef = useRef<(() => void) | null>(null);
	const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null
	);

	// The currently running view transition, so a superseding navigation can
	// skip it deterministically instead of leaving the browser to abort it.
	const activeTransitionRef = useRef<ViewTransition | null>(null);

	// Tracks the pathname (without query string) of the last route change so a
	// query-only navigation — e.g. search-as-you-type updating ?q= — can skip the
	// View Transition instead of snapshotting the whole page on every keystroke.
	const lastPathnameRef = useRef<string>(getInitialPath());

	const resolvePendingTransition = useCallback(() => {
		if (transitionTimeoutRef.current !== null) {
			clearTimeout(transitionTimeoutRef.current);
			transitionTimeoutRef.current = null;
		}

		if (pendingResolveRef.current) {
			const resolve = pendingResolveRef.current;
			pendingResolveRef.current = null;
			resolve();
		}
	}, []);

	const emit = useCallback(
		(
			type: RouterAction,
			payload: RouterPayload,
			transition?: NavigationTransition
		) => {
			const action: Action = {
				type,
				payload: {
					[type]: payload,
				},
			} as Action;

			// Reset/refresh stay synchronous and outside any view transition.
			if (type !== RouterAction.CurrentRoute) {
				dispatch(action);
				return;
			}

			// Route changes run inside a transition so React keeps the current
			// view mounted while the next view's suspended data loads, instead
			// of flashing a fallback.
			const dispatchRoute = () => startTransition(() => dispatch(action));

			// A query-only change (same pathname, different search string) must not
			// trigger a full-page View Transition — during search-as-you-type that
			// would snapshot the entire results grid on every debounced keystroke and
			// stall the page. Update the tracked pathname and fall through to a plain
			// dispatch.
			let nextPathname = lastPathnameRef.current;

			if (typeof payload === 'string') {
				nextPathname = pathnameOf(payload);
			}

			const isQueryOnlyChange = nextPathname === lastPathnameRef.current;

			lastPathnameRef.current = nextPathname;

			if (isQueryOnlyChange) {
				dispatchRoute();
				return;
			}

			// No View Transition API (Firefox, SSR): keep the existing behavior.
			if (!supportsViewTransition()) {
				dispatchRoute();
				return;
			}

			// A new navigation supersedes any in-flight transition; skip the
			// previous one and release its held snapshot so we never leak a
			// pending view transition.
			if (activeTransitionRef.current) {
				activeTransitionRef.current.skipTransition();
			}

			resolvePendingTransition();

			if (transition && transition !== 'default') {
				document.documentElement.dataset.transition = transition;
			} else {
				delete document.documentElement.dataset.transition;
			}

			// The callback returns a promise the View Transition API awaits before
			// taking the "after" snapshot. The old view is held on screen (by the
			// transition above) until the new view commits, at which point the
			// resolve-on-commit effect resolves this promise — giving a correct
			// old -> new cross-fade rather than old -> fallback.
			const viewTransition = document.startViewTransition(
				() =>
					new Promise<void>((resolve) => {
						pendingResolveRef.current = resolve;

						transitionTimeoutRef.current = setTimeout(
							resolvePendingTransition,
							VIEW_TRANSITION_TIMEOUT_MS
						);

						dispatchRoute();
					})
			);

			activeTransitionRef.current = viewTransition;

			// A skipped/superseded transition rejects `ready` with an AbortError —
			// that's expected, so swallow it instead of letting it surface as an
			// unhandled rejection. Anything else is a real error worth seeing.
			viewTransition.ready.catch((error: unknown) => {
				const wasSkipped =
					error instanceof DOMException && error.name === 'AbortError';

				if (!wasSkipped) {
					logRouterError(error as Error);
					reportRouterError(error as Error);
				}
			});

			viewTransition.finished.finally(() => {
				if (activeTransitionRef.current === viewTransition) {
					activeTransitionRef.current = null;
					delete document.documentElement.dataset.transition;
				}
			});
		},
		[dispatch, startTransition, resolvePendingTransition]
	);

	// Once the route transition settles (new view committed, or an error view
	// rendered via the ErrorBoundary), release the held snapshot so the View
	// Transition API animates to the final result.
	useEffect(() => {
		if (!isPending) {
			resolvePendingTransition();
		}
	}, [isPending, state.currentPath, resolvePendingTransition]);

	const store: RouterStateDispatch = useMemo(
		() => ({
			state,
			dispatch,
			emit,
			isPending,
		}),
		[state, dispatch, emit, isPending]
	);

	return (
		<RouterContext.Provider value={store}>{children}</RouterContext.Provider>
	);
};

export default RouterStateProvider;
